import type { Theme } from '@emotion/react';
import type {
  PercentileThreshold,
  StudentIntervention,
} from '@innovamat/glow-api-client';
import { hexToOpacity } from '@innovamat/radiance-utils';
import type { TFunction } from 'i18next';

export type ApexDiscretePoint = {
  seriesIndex: number;
  dataPointIndex: number;
  fillColor?: string;
  strokeColor?: string;
  size: number;
};

export type SeriesData = {
  name: string;
  displayName: string;
  uuid: string;
  data: Array<number | null>;
  hiddenMarkerIndices?: Set<number>;
  originalNullIndices?: Set<number>;
  firstValidIndex: number | null;
  rawData: Array<number | null>;
};

export const formatStudentName = (
  firstName?: string | null,
  lastName?: string | null
): string => {
  if (!firstName) {
    return 'Unknown';
  }

  if (!lastName || lastName.trim().length === 0) {
    return firstName;
  }
  const firstLetter = lastName.trim().charAt(0).toUpperCase();

  return `${firstName} ${firstLetter}.`;
};

export const calculateSeriesData = (
  students: StudentIntervention[],
  interpolateNullValues: (
    data: Array<number | null>,
    currentWeekIndex?: number
  ) => {
    interpolatedData: Array<number | null>;
    hiddenMarkerIndices: Set<number>;
    originalNullIndices: Set<number>;
    firstValidIndex: number | null;
  },
  currentWeekIndex?: number
): SeriesData[] => {
  return students.map((student) => {
    const rawData = student.weeks.map(
      (week) => week.arithmeticFluencyScore ?? null
    );
    const {
      interpolatedData,
      hiddenMarkerIndices,
      originalNullIndices,
      firstValidIndex,
    } = interpolateNullValues(rawData, currentWeekIndex);

    const studentName = formatStudentName(student.firstName, student.lastName);
    const studentUuid = student.uuid || '';

    return {
      name: `${studentName}::${studentUuid}`,
      displayName: studentName,
      uuid: studentUuid,
      data: interpolatedData,
      hiddenMarkerIndices,
      originalNullIndices,
      firstValidIndex,
      rawData,
    };
  });
};

export const calculateChartSeries = (
  seriesData: SeriesData[],
  maxWeeks: number | undefined,
  students: StudentIntervention[]
): Array<{ name: string; data: Array<number | null> }> => {
  return seriesData.map((series) => {
    const hasNoData = series.firstValidIndex === null;

    if (hasNoData) {
      const dataLength =
        maxWeeks || students[0]?.weeks.length || series.data.length;
      return {
        name: series.name,
        data: Array(dataLength).fill(null),
      };
    }
    return {
      name: series.name,
      data: series.data.map((value) =>
        value !== null ? Number(value.toFixed(1)) : null
      ),
    };
  });
};

export const generateWeekCategories = (
  maxWeeks: number | undefined,
  students: StudentIntervention[],
  currentWeek: number | undefined,
  t: TFunction
): string[] => {
  return (
    Array.from(
      { length: maxWeeks || students[0]?.weeks.length },
      (_, index) => {
        const isFirst = index === 0;
        const isEveryThree = (index + 1) % 3 === 0;
        const isCurrentWeek = index + 1 === currentWeek;
        const isLastWeek = index + 1 === maxWeeks;

        if (isFirst || isEveryThree || isCurrentWeek || isLastWeek) {
          return t('reports.catchup-up.subtitle.numberOfTheWeek', {
            numOfTheWeek: index + 1,
          });
        }

        return '';
      }
    ) || []
  );
};

export const calculateCurrentWeekIndex = (
  currentWeek: number | undefined
): number => {
  return currentWeek && currentWeek > 0 ? currentWeek - 1 : -1;
};

export const calculateYAxisMax = (
  students: StudentIntervention[],
  percentiles: PercentileThreshold[]
): number => {
  const percentileFinalValue =
    percentiles && percentiles.length > 0
      ? Math.max(...percentiles.map((p) => p.finalValue))
      : 0;

  const allStudentValues = students.flatMap((student) =>
    student.weeks
      .map((week) => week.arithmeticFluencyScore)
      .filter((score): score is number => score !== null && score !== undefined)
  );

  const maxStudentValue =
    allStudentValues.length > 0 ? Math.max(...allStudentValues) : 0;

  return (
    (maxStudentValue > percentileFinalValue
      ? maxStudentValue
      : percentileFinalValue) + 2
  );
};

const createHiddenMarker = (
  seriesIndex: number,
  dataPointIndex: number
): ApexDiscretePoint => ({
  seriesIndex,
  dataPointIndex,
  size: 0,
});

const createNoDataMarker = (
  seriesIndex: number,
  dataPointIndex: number,
  neutralColor: string,
  strokeColor: string
): ApexDiscretePoint => ({
  seriesIndex,
  dataPointIndex,
  size: 5,
  fillColor: neutralColor,
  strokeColor: strokeColor,
});

const shouldShowCurrentWeekNoData = (
  series: SeriesData,
  currentWeekIndex: number
): boolean => {
  return (
    currentWeekIndex >= 0 &&
    series.originalNullIndices?.has(currentWeekIndex) === true
  );
};

const shouldShowFirstWeekNoData = (series: SeriesData): boolean => {
  return (
    series.originalNullIndices?.has(0) === true &&
    series.firstValidIndex !== null
  );
};

const shouldSkipHiddenMarker = (
  dataPointIndex: number,
  currentWeekIndex: number,
  showCurrentWeekNoData: boolean
): boolean => {
  return showCurrentWeekNoData && dataPointIndex === currentWeekIndex;
};

export const generateDiscreteMarkers = (
  seriesData: SeriesData[],
  currentWeekIndex: number,
  theme: Theme
): ApexDiscretePoint[] => {
  const neutralColor = theme.tokens.color.global.neutral.neutral300.value;
  const strokeColor = theme.tokens.color.global.white.value;
  const discreteMarkers: ApexDiscretePoint[] = [];

  seriesData.forEach((series, seriesIndex) => {
    const showCurrentWeekNoData = shouldShowCurrentWeekNoData(
      series,
      currentWeekIndex
    );

    if (series.hiddenMarkerIndices) {
      series.hiddenMarkerIndices.forEach((dataPointIndex) => {
        if (
          !shouldSkipHiddenMarker(
            dataPointIndex,
            currentWeekIndex,
            showCurrentWeekNoData
          )
        ) {
          discreteMarkers.push(createHiddenMarker(seriesIndex, dataPointIndex));
        }
      });
    }

    if (shouldShowFirstWeekNoData(series)) {
      discreteMarkers.push(
        createNoDataMarker(seriesIndex, 0, neutralColor, strokeColor)
      );
    }

    if (showCurrentWeekNoData) {
      discreteMarkers.push(
        createNoDataMarker(
          seriesIndex,
          currentWeekIndex,
          neutralColor,
          strokeColor
        )
      );
    }
  });

  return discreteMarkers;
};

export const calculateAnnotationPosition = (
  currentWeek: number | undefined,
  totalWeeks: number,
  currentWeekIndex: number,
  chartWidth: number,
  annotationWidth: number
): { x: number; x2: number } | null => {
  if (!currentWeek || !totalWeeks || currentWeekIndex < 0) {
    return null;
  }
  const misalignment = currentWeekIndex / 2;

  const weekPosition = (currentWeekIndex / totalWeeks) * chartWidth;
  const x = weekPosition - annotationWidth / 2 - misalignment;
  const x2 = weekPosition + annotationWidth / 2 - misalignment;

  return { x, x2 };
};

const calculatePercentileValue = (
  initialValue: number,
  finalValue: number,
  weekIndex: number,
  totalWeeks: number
): number => {
  if (totalWeeks <= 1) {
    return initialValue;
  }

  if (weekIndex === 0) {
    return initialValue;
  }

  if (weekIndex === totalWeeks - 1) {
    return finalValue;
  }

  const slope = (finalValue - initialValue) / (totalWeeks - 1);
  return initialValue + slope * weekIndex;
};

const generatePercentileData = (
  percentile: PercentileThreshold,
  totalWeeks: number
): number[] => {
  return Array.from({ length: totalWeeks }, (_, weekIndex) =>
    calculatePercentileValue(
      percentile.initialValue,
      percentile.finalValue,
      weekIndex,
      totalWeeks
    )
  );
};

const createPercentileHiddenMarkers = (
  seriesIndex: number,
  dataLength: number
): ApexDiscretePoint[] => {
  return Array.from({ length: dataLength }, (_, dataPointIndex) =>
    createHiddenMarker(seriesIndex, dataPointIndex)
  );
};

export const generatePercentileSeries = (
  percentiles: PercentileThreshold[],
  totalWeeks: number,
  baseSeriesIndex: number
): {
  series: Array<{ name: string; data: number[] }>;
  seriesIndices: number[];
  discreteMarkers: ApexDiscretePoint[];
} => {
  const percentileSeries: Array<{ name: string; data: number[] }> = [];
  const percentileSeriesIndices: number[] = [];
  const discreteMarkers: ApexDiscretePoint[] = [];

  percentiles.forEach((percentile) => {
    const percentileData = generatePercentileData(percentile, totalWeeks);
    const percentileSeriesIndex = baseSeriesIndex + percentileSeries.length;

    percentileSeriesIndices.push(percentileSeriesIndex);
    percentileSeries.push({
      name: 'Percentile30',
      data: percentileData,
    });

    const hiddenMarkers = createPercentileHiddenMarkers(
      percentileSeriesIndex,
      percentileData.length
    );
    discreteMarkers.push(...hiddenMarkers);
  });

  return {
    series: percentileSeries,
    seriesIndices: percentileSeriesIndices,
    discreteMarkers,
  };
};

export const getChartColors = (
  theme: Theme,
  totalSeries: number,
  percentile30Indices: number[]
): string[] => {
  const studentColors = [
    theme.tokens.color.global.blue.blue500.value,
    theme.tokens.color.global.green.green500.value,
    theme.tokens.color.global.purple.purple500.value,
    theme.tokens.color.global.orange.orange500.value,
    theme.tokens.color.global.pink.pink500.value,
    theme.tokens.color.global.teal.teal500.value,
    theme.tokens.color.global.red.red500.value,
    theme.tokens.color.global.yellow.yellow500.value,
    theme.tokens.color.global.neutral.neutral700.value,
  ];

  const percentileColorRaw =
    theme.tokens.color.specific.reports.tables['divider-primary'].value;
  const percentileColor = hexToOpacity(percentileColorRaw, 0.5);

  return Array.from({ length: totalSeries }, (_, index) => {
    if (percentile30Indices.includes(index)) {
      return percentileColor;
    }
    return studentColors[index % studentColors.length];
  });
};
